import type { BillingDocument, BillingState, Client, DocumentType, Expense, LineItem, Service } from "../types";
import { companyDefaults, defaultServices, demoState } from "../data/defaults";

export const PRIMARY_STORAGE_KEY = "revivalai-billing-shared";
export const BACKUP_STORAGE_KEY = "revival-billing-hub-v1";

export const uid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "id-" + Math.random().toString(36).slice(2, 11);
};

export const today = () => new Date().toISOString().slice(0, 10);

export const money = (value: number | string) => {
  const num = typeof value === "string" ? parseFloat(value) || 0 : value || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(num);
};

export const formatDate = (dateStr?: string) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return dateStr;
  }
};

export function totals(items: LineItem[], gstRate: number) {
  const subtotal = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.rate) || 0), 0);
  const net = items.reduce((s, i) => {
    const itemTotal = (Number(i.qty) || 0) * (Number(i.rate) || 0);
    const disc = Number(i.disc) || 0;
    return s + (itemTotal * (1 - disc / 100));
  }, 0);
  const discTotal = Math.max(0, subtotal - net);
  const gstAmt = (net * (Number(gstRate) || 0)) / 100;
  const total = net + gstAmt;
  return { subtotal, discTotal, gstAmt, total };
}

export function nextNumber(type: DocumentType, docs: BillingDocument[]) {
  const prefix = type === "invoice" ? "INV" : "QUO";
  const max = docs
    .filter((d) => d.type === type)
    .reduce((m, d) => Math.max(m, Number(d.number.match(/(\d+)$/)?.[1] || 0)), 0);
  return `${prefix}-${String(max + 1).padStart(4, "0")}`;
}

export function blankDocument(type: DocumentType, state: BillingState): BillingDocument {
  const docs = type === "invoice" ? state.invoices : state.quotes;
  return {
    id: uid(),
    type,
    number: nextNumber(type, docs),
    date: today(),
    due: today(),
    clientId: "",
    company: "",
    email: "",
    phone: "",
    address: "",
    clientGstin: "",
    pos: "Karnataka",
    items: [
      {
        id: uid(),
        desc: "",
        hsn: "998361",
        qty: 1,
        unit: "Month",
        rate: 0,
        disc: 0,
        amount: 0,
      },
    ],
    subtotal: 0,
    discTotal: 0,
    gstRate: state.settings?.gst_rate ?? 18,
    gstAmt: 0,
    total: 0,
    notes: state.settings?.notes || companyDefaults.notes,
    po: "",
    currency: "INR",
    paymentMethod: "Bank Transfer / NEFT",
    status: "draft",
    createdAt: new Date().toISOString(),
  };
}

export const blankClient = (): Client => ({
  id: uid(),
  name: "",
  company: "",
  email: "",
  phone: "",
  address: "",
  gstin: "",
  industry: "",
});

export const blankService = (): Service => ({
  id: uid(),
  serviceId: uid(),
  name: "",
  category: "Digital Marketing",
  unit: "Month",
  billing: "monthly",
  rate: 0,
  hsn: "998361",
  description: "",
  delivery: "Immediate",
  platforms: [],
  note: "",
  features: [],
  bestFor: [],
  bonus: [],
  currency: "INR",
});

export const blankExpense = (): Expense => ({
  id: uid(),
  date: today(),
  category: "Software",
  description: "",
  vendor: "",
  amount: 0,
  recurrence: "one_time",
  paymentMethod: "Bank Transfer / NEFT",
  notes: "",
});

/**
 * Hydrates state from LocalStorage:
 * Checks 'revivalai-billing-shared' first, then 'revival-billing-hub-v1',
 * and seamlessly merges with seeded demo state if any collections are empty.
 */
export function loadPersistedState(): BillingState {
  try {
    const sharedRaw = localStorage.getItem(PRIMARY_STORAGE_KEY);
    const backupRaw = localStorage.getItem(BACKUP_STORAGE_KEY);

    let parsedShared: Partial<BillingState> | null = null;
    let parsedBackup: Partial<BillingState> | null = null;

    if (sharedRaw) {
      try { parsedShared = JSON.parse(sharedRaw); } catch (e) { console.warn("Failed parsing shared storage", e); }
    }
    if (backupRaw) {
      try { parsedBackup = JSON.parse(backupRaw); } catch (e) { console.warn("Failed parsing backup storage", e); }
    }

    const state: BillingState = structuredClone(demoState);

    // Invoices
    if (parsedShared?.invoices && Array.isArray(parsedShared.invoices) && parsedShared.invoices.length > 0) {
      state.invoices = parsedShared.invoices;
    } else if (parsedBackup?.invoices && Array.isArray(parsedBackup.invoices) && parsedBackup.invoices.length > 0) {
      state.invoices = parsedBackup.invoices;
    }

    // Quotes
    if (parsedBackup?.quotes && Array.isArray(parsedBackup.quotes) && parsedBackup.quotes.length > 0) {
      state.quotes = parsedBackup.quotes;
    }

    // Clients
    if (parsedBackup?.clients && Array.isArray(parsedBackup.clients) && parsedBackup.clients.length > 0) {
      state.clients = parsedBackup.clients;
    }

    // Services
    if (parsedBackup?.services && Array.isArray(parsedBackup.services) && parsedBackup.services.length > 0) {
      state.services = parsedBackup.services;
    }

    // Expenses
    if (parsedBackup?.expenses && Array.isArray(parsedBackup.expenses) && parsedBackup.expenses.length > 0) {
      state.expenses = parsedBackup.expenses;
    }

    // Settings
    if (parsedBackup?.settings && typeof parsedBackup.settings === "object") {
      state.settings = { ...companyDefaults, ...parsedBackup.settings };
    }

    return state;
  } catch (err) {
    console.error("Error loading persisted state:", err);
    return structuredClone(demoState);
  }
}

/**
 * Saves state simultaneously to both storage keys for maximum compatibility.
 */
export function persistState(state: BillingState) {
  try {
    // Save to primary shared schema
    localStorage.setItem(PRIMARY_STORAGE_KEY, JSON.stringify({
      invoices: state.invoices,
      quotes: state.quotes,
      clients: state.clients,
      services: state.services,
      expenses: state.expenses,
      settings: state.settings,
      updatedAt: new Date().toISOString()
    }));

    // Save to app backup schema
    localStorage.setItem(BACKUP_STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    console.error("Failed to persist billing state to LocalStorage", err);
  }
}
